import { faPlus, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { memo, useCallback,useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { WhiteBox } from '../../../baseComponents/AppLayout/WhiteBox'
import { ButtonPrimary } from '../../../baseComponents/Button'
import { joinAndRemoveDuplicatedBars } from '../../../baseComponents/CreateRoutering/functions'
import { ComponentWithPermissions } from '../../../baseComponents/CreateRoutering/types'
import PermissionChecker from '../../../baseComponents/PermissionChecker'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { CreateLinkProps, getBasePath } from '../../../utils/globals/link'
import { getConfigParam, getSingleEmployeeParam, getUserInfo } from '../../../utils/reducers/getters'
import { addBottomNavAppElement, changeEmployeeParams } from '../../../utils/reducers/reduxDispatch'
import TaskTable from './TaskTable'
import { TableWhereSearchBarTy, TaskScreenTableBaseTy, TaskScreenTableInfoTy } from './types'
import useTaskConfigColumns from '../../../utils/hooks/tasks/useTaskConfigColumns'
import { ButtonTaskConfigColumnsWithModal } from './ModalTaskConfigColumnsTabs'
import { DictTaskTableHeaderTy, DictTaskTableHeaderTyExt, TaskTableHeaderTyExt } from './ModalTaskConfigColumnsTabs/types'
import { memoChecker } from '../../../utils/globals/components'
import { TableInstanceType } from '../../../baseComponents/TwinTable/types'
import CustomSelect from '../../../forms/CustomSelect'
import { InputCalendarStateFull } from '../../../forms/Input/InputCalendar'
import useTaskHeaderSelectorsDict from '../../../utils/hooks/tasks/useTaskHeaderSelectorsDict'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import NullComponent from '../../../baseComponents/NullComponent'
import { ColumnConfiguration } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { flushSync } from 'react-dom'
import { SearchBarTable } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents'
import TwinTrans from '../../../baseComponents/TwinTrans'


interface TaskScreenTableWhiteBoxProps extends TaskScreenTableProps { }

export const TaskScreenTableWhiteBox: React.FC<TaskScreenTableWhiteBoxProps> = ({ extraWhereParams, ...rest }) => {
    return (
        <WhiteBox className='flex flex-auto py-40 px-50 task_table_wb'>
            <TaskScreenTable {...rest} extraWhereParams={{ ...extraWhereParams }} />
        </WhiteBox>
    )
}

type TaskScreenTableProps = ComponentWithPermissions & TaskScreenTableLogicProps & TaskScreenTableBaseTy & {
    pageData: TaskScreenTableInfoTy
    name: string
    createRowDataLink: CreateLinkProps
    floatingTasks: boolean
    extraCreateTaskParams?: TwinDictionary
    extraHeaders?: { [key: string]: React.FC<any> }
}

const TaskScreenTable: React.FC<TaskScreenTableProps> = ({ name, columns, getDataFrom, createRowDataLink, listenSocketStr, pageData, extraWhereParams, floatingTasks, userPermissions, ...rest }) => {
    const { createTask, headers, setTableInstance, defaultHeaderParams, selectedColsFiltersHeader, setSelectedColsFiltersHeader, memorizedColumns, tableInstance, memorizedHeaders, opened, setOpened } = useTaskScreenTableLogic({ floatingTasks, columns, name, ...rest })
    const renderMyHeader = renderHeader(headers, memorizedHeaders)
    return (
        <div className='task_screen_table flex flex-col flex-auto w-1'>
            <div className='flex justify-between items-center flex-wrap gap-10 mb-20 task_table_header'>
                <div className='regular18 task_table_title'>{pageData.title}</div>
                <div className='flex items-center flex-wrap-reverse gap-10'>
                    <ButtonTaskConfigColumnsWithModal name={name} columns={columns} setSelectedColsFiltersHeader={setSelectedColsFiltersHeader} selectedColsFiltersHeader={selectedColsFiltersHeader} headers={headers} tableInstance={tableInstance} />
                    <PermissionChecker userPermissions={userPermissions} permission='create'>
                        <ButtonPrimary onClick={createTask}>
                            <TwinIcon icon={faPlus} className='mr-8' />
                            {pageData?.newText}
                        </ButtonPrimary>
                    </PermissionChecker>
                </div>
            </div> 
            <div className={'cursor-pointer items-center task_table_filter_dropdown hidden regular14 ' + (opened ? '' : 'mb-20')} onClick={() => setOpened((old) => !old)}>
                <span className='mr-10'><TwinTrans transKey='filters'>Filtros</TwinTrans></span>
                <TwinIcon icon={opened ? faChevronDown : faChevronUp} className='ml-auto mr-10' />
            </div>
            <div className={'flex gap-x-15 items-end mb-20 mt-15 task_table_filters ' + (opened ? 'active' : '')}>
                {tableInstance ? renderMyHeader : null}
            </div>
            <TaskTable generalSearchbar={selectedColsFiltersHeader?.filterHeader?.generalSearchBar} {...{ name, getDataFrom, listenSocketStr, columns, createRowDataLink, floatingTasks, selectedColsFiltersHeader, setSelectedColsFiltersHeader, memorizedColumns, extraWhereParams, setTableInstance, defaultHeaderParams }} />
        </div>
    )
}


interface TaskScreenTableLogicProps {
    name: string
    limitPath: string
    floatingTasks: boolean
    extraCreateTaskParams?: TwinDictionary
    columns: ColumnConfiguration[]
}
export const useTaskScreenTableLogic = ({ name, limitPath, floatingTasks, extraCreateTaskParams, columns }: TaskScreenTableLogicProps) => {
    const [tableInstance, setTableInstance] = useState<TableInstanceType>(null)
    const [opened, setOpened] = useState(true)
    const { t } = useTwinTranslation()
    const configHeaders: DictTaskTableHeaderTy = {
        'general_searchbar': {
            id: 'general_searchbar',
            label: t('searchBar', 'Barra de búsqueda'),
        },
        'start_createdAt': {
            id: 'start_createdAt',
            label: t('from', 'Desde') + ' (' + t('createdAtDate', 'Fecha creación') + ')',
        },
        'end_createdAt': {
            id: 'end_createdAt',
            label: t('to', 'Hasta') + ' (' + t('createdAtDate', 'Fecha creación') + ')',
        },
        'start_date': {
            id: 'start_date',
            label: t('from', 'Desde') + ' (' + t('assignedDate', 'Fecha asignada') + ')',
            hideByDefault: true
        },
        'end_date': {
            id: 'end_date',
            label: t('to', 'Hasta') + ' (' + t('assignedDate', 'Fecha asignada') + ')',
            hideByDefault: true
        },
        'finished': {
            id: 'finished',
            label: t('status', 'Estado'),
        },
        'assigned': {
            id: 'assigned',
            label: t('assignation', 'Asignación'),
        },
        'visibles': {
            id: 'visibles',
            label: t('visibles', 'Visibles'),
        },
        'subTasks': {
            id: 'subTasks',
            label: t('subTasks', 'Subtareas'),
        },
        'deletedF': {
            id: 'deletedF',
            label: t('deletedF', 'Eliminadas'),
        },
    }
    const defaultHeaderParams = useMemo(() => {
        return {
            generalSearchBar: '',
            start_date: '',
            end_date: '',
            finished: 1,
            visibles: 0,
            assigned: 1,
            subtask: 1,
            deletedF: 'false'
        }
    }, [])
    const { selectedColsFiltersHeader, setSelectedColsFiltersHeader, memorizedColumns, memorizedHeaders } = useTaskConfigColumns({ name, columns, headers: configHeaders })

    const { dictAssignation, dictFinished, dictSubTasks, dictVisibles, dictDeleted } = useTaskHeaderSelectorsDict()

    const finishedFilterValue = selectedColsFiltersHeader?.filters['finished']?.value || selectedColsFiltersHeader?.filterHeader?.['finished'] || defaultHeaderParams?.['finished']
    const assignedFilterValue = selectedColsFiltersHeader?.filters['assigned']?.value || selectedColsFiltersHeader?.filterHeader?.['assigned'] || defaultHeaderParams?.['assigned']
    const visiblesFilterValue = selectedColsFiltersHeader?.filters['visibles']?.value || selectedColsFiltersHeader?.filterHeader?.['visibles'] || defaultHeaderParams?.['visibles']
    const subtaskFilterValue = selectedColsFiltersHeader?.filters['subtask']?.value || selectedColsFiltersHeader?.filterHeader?.['subtask'] || defaultHeaderParams?.['subtask']
    const deletedFilterValue = selectedColsFiltersHeader?.filters['deletedF']?.value || selectedColsFiltersHeader?.filterHeader?.['deletedF'] || defaultHeaderParams?.['deletedF']
    const saveTaskTableDefaultFilterWhenChanges = useMemo(() => getSingleEmployeeParam('saveTaskTableDefaultFilterWhenChanges') === 'on', [])

    const changeHeaderTableWhereParams = useCallback((field: keyof TableWhereSearchBarTy, value: string | number) => {
        flushSync(async () => {
            let mustUpdate = true
            let result = null
            await setSelectedColsFiltersHeader((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                let myHeaders = copyOld.filterHeader || {}
                let myFilters = copyOld.filters || {}
                if ((myHeaders?.[field] !== undefined && myHeaders?.[field] === value) || (value === "" && myHeaders?.[field] === undefined)) {
                    mustUpdate = false
                    return old
                }
                if (field === 'generalSearchBar' || field === 'end_createdAt' || field === 'end_date' || field === 'start_date' || field === 'start_createdAt'){
                    result = { ...copyOld, filterHeader: { ...myHeaders, [field]: value }}
                }else {
                    result = { ...copyOld, filterHeader: { ...myHeaders, [field]: value }, filters: { ...myFilters, [field]: { value: value } } }
                }
                return result
            })
            if (mustUpdate) {
                tableInstance?.getTableDataFromStart()
            }
            if (result) {
                let myResult = result as any
                const myName = 'table_' + name
                let isCustomFilter = myResult?.id && String(myResult?.id) !== '0' ? true : false
                if (!isCustomFilter && myResult && saveTaskTableDefaultFilterWhenChanges) {
                    delete myResult.id
                    delete myResult.name
                    const myValuesStr = JSON.stringify(myResult)
                    await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValuesStr })
                    changeEmployeeParams({ [myName]: myValuesStr })
                }
            }
        })
    }, [setSelectedColsFiltersHeader, tableInstance, name, saveTaskTableDefaultFilterWhenChanges])

    const headers: DictTaskTableHeaderTyExt = {
        'general_searchbar': {
            ...configHeaders.general_searchbar,
            Component: SearchBarTable,
            props: { className: 'mr-auto', placeholder: t('searchKeywordDots', 'Busca por palabra clave...'), onChange: (value: string) => changeHeaderTableWhereParams('generalSearchBar', value), value: selectedColsFiltersHeader?.filterHeader?.generalSearchBar }
        },
        'start_createdAt': {
            ...configHeaders.start_createdAt,
            Component: InputCalendarStateFull,
            props: { allowEmptyEffect: true, placeholder: t('selectDate', 'Selecciona una fecha'), label: t('createdFrom', 'Creado Desde'), onlyValids: true, value: selectedColsFiltersHeader?.filterHeader?.['start_createdAt'], onChange: (value: string) => changeHeaderTableWhereParams('start_createdAt', value) }
        },
        'end_createdAt': {
            ...configHeaders.end_createdAt,
            Component: InputCalendarStateFull,
            props: { allowEmptyEffect: true, placeholder: t('selectDate', 'Selecciona una fecha'), label: t('createdTo', 'Creado Hasta'), onlyValids: true, value: selectedColsFiltersHeader?.filterHeader?.['end_createdAt'], onChange: (value: string) => changeHeaderTableWhereParams('end_createdAt', value) }
        },
        'start_date': {
            ...configHeaders.start_date,
            Component: InputCalendarStateFull,
            props: { allowEmptyEffect: true, placeholder: t('selectDate', 'Selecciona una fecha'), label: t('assignedFrom', 'Asign. Desde'), onlyValids: true, value: selectedColsFiltersHeader?.filterHeader?.['start_date'], onChange: (value: string) => changeHeaderTableWhereParams('start_date', value) },
        },
        'end_date': {
            ...configHeaders.end_date,
            Component: InputCalendarStateFull,
            props: { allowEmptyEffect: true, placeholder: t('selectDate', 'Selecciona una fecha'), label: t('assignedTo', 'Asign. Hasta'), onlyValids: true, value: selectedColsFiltersHeader?.filterHeader?.['end_date'], onChange: (value: string) => changeHeaderTableWhereParams('end_date', value) },
        },
        'finished': {
            ...configHeaders.finished,
            Component: CustomSelect,
            props: { className: 'w-120 task', label: t('status', 'Estado'), items: dictFinished, value: finishedFilterValue, onChange: (value: string) => changeHeaderTableWhereParams('finished', value) }
        },
        'assigned': {
            ...configHeaders.assigned,
            Component: name !== 'tableTasksByUser' ? CustomSelect : NullComponent,
            props: { className: 'w-120 task', label: t('assignation', 'Asignación'), items: dictAssignation, value: assignedFilterValue, onChange: (value: string) => changeHeaderTableWhereParams('assigned', value) }
        },
        'visibles': {
            ...configHeaders.visibles,
            Component: CustomSelect,
            props: { className: 'w-120 task', label: t('visibles', 'Visibles'), items: dictVisibles, value: visiblesFilterValue, onChange: (value: string) => changeHeaderTableWhereParams('visibles', value) },
        },
        'subTasks': {
            ...configHeaders.subTasks,
            Component: CustomSelect,
            props: { className: 'w-120 task', label: t('subTasks', 'Subtareas'), items: dictSubTasks, value: subtaskFilterValue, onChange: (value: string) => changeHeaderTableWhereParams('subtask', value) },
        },
        'deletedF': {
            ...configHeaders.deletedF,
            Component: CustomSelect,
            props: { className: 'w-120 task', label: t('deletedF', 'Eliminadas'), items: dictDeleted, value: deletedFilterValue, onChange: (value: string) => changeHeaderTableWhereParams('deletedF', value) },
            hideByDefault: true,
        },
    }

    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, limitPath)
    const user = getUserInfo()
    const createTask = useCallback(async () => {
        let WorkingCenterId = undefined
        const workingCenterByDefault = getConfigParam('assignTaskYourWorkingCenterByDefault')
        if (workingCenterByDefault && workingCenterByDefault === 'on') {
            WorkingCenterId = user?.WorkingCenterId
        }
        const createdTask = await twinFetchPostJSON('/api/app/task/createTask', { WorkingCenterId, ...extraCreateTaskParams })
        if (createdTask) {
            if (floatingTasks) {
                addBottomNavAppElement({ type: 'taskModal', key: 'taskModal-' + createdTask.id, extraData: { id: createdTask.id, opened: true } })
            } else {
                navigate(joinAndRemoveDuplicatedBars(basePath, '/' + createdTask.id))
            }
        }
    }, [navigate, basePath, user?.WorkingCenterId, floatingTasks, extraCreateTaskParams])

    return { createTask, headers, setTableInstance, defaultHeaderParams, selectedColsFiltersHeader, setSelectedColsFiltersHeader, memorizedColumns, tableInstance, memorizedHeaders, opened, setOpened }
}

const renderHeader = (header: DictTaskTableHeaderTyExt, selectedHeader: TwinDictionary) => {
    const renderHeader: JSX.Element[] = []
    for (const key in selectedHeader) {
        const element = header[key]
        renderHeader.push(<TaskTableHeaderRenderer params={element} key={key} />)
    }
    return renderHeader
}
interface TaskTableHeaderRendererProps {
    params: TaskTableHeaderTyExt

}

export const TaskTableHeaderRenderer: React.FC<TaskTableHeaderRendererProps> = memo(({ params }) => {
    const MyComponent = params?.Component
    if (MyComponent) {
        return <MyComponent key={params.id} {...params.props} />
    }
    return null
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['params']))



export default TaskScreenTable